<template>
  <div class="invite-page">
    <section class="invite-toolbar">
      <div class="invite-toolbar__copy">
        <div class="invite-toolbar__title">邀请码管理</div>
        <div class="invite-toolbar__desc">
          发出去的邀请码。会员在小程序「我的 - 邀请码」里填写后完成绑定，
          头像下方会展示这里配置的标识文案。
        </div>
      </div>
      <div class="invite-toolbar__actions">
        <span class="invite-count">共 {{ total }} 个</span>
        <button class="ghost-button" type="button" :disabled="loading" @click="reload">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
        <button class="primary-button" type="button" @click="startCreate">新建邀请码</button>
      </div>
    </section>

    <section class="invite-card">
      <div class="invite-filters">
        <input
          v-model="filters.keyword"
          class="filter-input"
          type="text"
          placeholder="搜索邀请码 / 展示文案"
          @keyup.enter="reload"
        />
        <select v-model="filters.status" class="filter-select" @change="reload">
          <option value="">全部状态</option>
          <option value="1">启用中</option>
          <option value="0">已停用</option>
        </select>
        <button class="primary-button" type="button" @click="reload">搜索</button>
      </div>

      <div v-if="loading" class="list-state">正在加载邀请码…</div>
      <div v-else-if="!rows.length" class="list-state">还没有邀请码，点右上角「新建邀请码」。</div>
      <table v-else class="invite-table">
        <thead>
          <tr>
            <th>邀请码</th>
            <th>头像下方展示</th>
            <th>已绑定</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="String(row.id)">
            <td>
              <span class="code-text">{{ row.code }}</span>
            </td>
            <td>
              <span class="badge-preview" :class="badgeClass(row.badgeStyle)">
                <span class="badge-preview__dot"></span>{{ row.badgeText }}
              </span>
            </td>
            <td>
              {{ row.usedCount || 0 }} 人
              <span v-if="row.maxUses" class="used-limit">/ 上限 {{ row.maxUses }}</span>
            </td>
            <td>
              <span class="status-pill" :class="row.status === 1 ? 'status-pill--on' : 'status-pill--off'">
                {{ row.status === 1 ? "启用中" : "已停用" }}
              </span>
            </td>
            <td class="row-actions">
              <button class="link-button" type="button" @click="startEdit(row)">编辑</button>
              <button class="link-button" type="button" @click="viewUsers(row)">绑定用户</button>
              <button class="link-button" type="button" @click="toggleStatus(row)">
                {{ row.status === 1 ? "停用" : "启用" }}
              </button>
              <button class="link-button link-button--danger" type="button" @click="removeCode(row)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="total > pageSize" class="pager">
        <button class="ghost-button" type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
        <span class="pager-text">第 {{ page }} / {{ totalPages }} 页</span>
        <button class="ghost-button" type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
      </div>
    </section>

    <section class="invite-card">
      <div class="editor-head">
        <div>
          <div class="editor-eyebrow">{{ form.id ? "正在编辑" : "新建" }}</div>
          <h2>{{ form.id ? form.code : "新建邀请码" }}</h2>
        </div>
        <span class="editor-hint">邀请码留空会自动生成</span>
      </div>

      <div class="editor-grid">
        <label class="field">
          <span class="field-label">邀请码</span>
          <input v-model="form.code" class="field-input" type="text" placeholder="留空自动生成，例如 BAISE2026" />
        </label>
        <label class="field">
          <span class="field-label">头像下方展示文案</span>
          <input v-model="form.badgeText" class="field-input" type="text" placeholder="例如：纯白社群会员" maxlength="16" />
        </label>
        <label class="field">
          <span class="field-label">展示样式</span>
          <select v-model="form.badgeStyle" class="field-input">
            <option value="gold">金色（会员标识）</option>
            <option value="purple">紫色</option>
            <option value="blue">蓝色</option>
            <option value="green">绿色</option>
          </select>
        </label>
        <label class="field">
          <span class="field-label">可绑定人数上限</span>
          <input v-model.number="form.maxUses" class="field-input" type="number" min="1" placeholder="留空表示不限" />
        </label>
        <label class="field">
          <span class="field-label">状态</span>
          <select v-model.number="form.status" class="field-input">
            <option :value="1">启用</option>
            <option :value="0">停用</option>
          </select>
        </label>
        <label class="field field--full">
          <span class="field-label">备注</span>
          <input v-model="form.remark" class="field-input" type="text" placeholder="发给谁、什么时候发的，方便后面查" />
        </label>
      </div>

      <div class="editor-preview">
        <span class="editor-preview__label">小程序头像下方效果</span>
        <span class="badge-preview" :class="badgeClass(form.badgeStyle)">
          <span class="badge-preview__dot"></span>{{ form.badgeText || "展示文案" }}
        </span>
      </div>

      <div class="editor-actions">
        <span v-if="message" class="editor-message" :class="{ 'editor-message--error': messageError }">{{ message }}</span>
        <button v-if="form.id" class="ghost-button" type="button" @click="startCreate">取消编辑</button>
        <button class="primary-button" type="button" :disabled="saving" @click="submit">
          {{ saving ? "保存中…" : (form.id ? "保存修改" : "创建邀请码") }}
        </button>
      </div>
    </section>

    <div v-if="usersVisible" class="modal-mask" @click="usersVisible = false">
      <div class="modal-panel" @click.stop>
        <div class="modal-head">
          <div>
            <div class="editor-eyebrow">绑定用户</div>
            <h3>{{ usersTitle }}</h3>
          </div>
          <button class="link-button" type="button" @click="usersVisible = false">关闭</button>
        </div>
        <div v-if="usersLoading" class="list-state">正在加载…</div>
        <div v-else-if="!users.length" class="list-state">还没有人绑定这个邀请码。</div>
        <div v-else class="user-list">
          <div v-for="user in users" :key="String(user.userId)" class="user-row">
            <img v-if="user.avatarUrl" class="user-avatar" :src="resolveFileUrl(user.avatarUrl)" alt="" />
            <div v-else class="user-avatar user-avatar--fallback">{{ (user.nickname || "用").slice(0, 1) }}</div>
            <div class="user-copy">
              <div class="user-name">{{ user.nickname || "用户" }}</div>
              <div class="user-meta">UID {{ user.userId }} · {{ formatTime(user.redeemedAt) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"
import {
  deleteInviteCode,
  fetchInviteCodeUsers,
  fetchInviteCodes,
  saveInviteCode,
  updateInviteCodeStatus
} from "../api/inviteCode"

function emptyForm() {
  return {
    id: null,
    code: "",
    badgeText: "",
    badgeStyle: "gold",
    maxUses: "",
    status: 1,
    remark: ""
  }
}

export default {
  name: "InviteCodeView",
  data() {
    return {
      loading: false,
      saving: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { keyword: "", status: "" },
      form: emptyForm(),
      message: "",
      messageError: false,
      usersVisible: false,
      usersLoading: false,
      users: [],
      usersTitle: ""
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / this.pageSize))
    }
  },
  created() {
    this.reload()
  },
  methods: {
    resolveFileUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    formatTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    },
    badgeClass(style) {
      return `badge-preview--${style || "gold"}`
    },
    async reload() {
      this.loading = true
      try {
        const res = await fetchInviteCodes({
          keyword: this.filters.keyword || undefined,
          status: this.filters.status === "" ? undefined : Number(this.filters.status),
          page: this.page,
          pageSize: this.pageSize
        })
        const data = res.data || {}
        this.rows = data.records || []
        this.total = Number(data.total || 0)
      } catch (error) {
        this.setMessage((error && error.message) || "邀请码加载失败", true)
      } finally {
        this.loading = false
      }
    },
    changePage(next) {
      this.page = Math.min(Math.max(1, next), this.totalPages)
      this.reload()
    },
    startCreate() {
      this.form = emptyForm()
      this.message = ""
      this.messageError = false
    },
    startEdit(row) {
      this.form = {
        id: row.id,
        code: row.code || "",
        badgeText: row.badgeText || "",
        badgeStyle: row.badgeStyle || "gold",
        maxUses: row.maxUses || "",
        status: row.status === 0 ? 0 : 1,
        remark: row.remark || ""
      }
      this.message = ""
      this.messageError = false
    },
    async submit() {
      if (this.saving) return
      if (!this.form.badgeText) {
        this.setMessage("请填写头像下方展示文案", true)
        return
      }
      this.saving = true
      try {
        const payload = {
          id: this.form.id || undefined,
          code: this.form.code || undefined,
          badgeText: this.form.badgeText,
          badgeStyle: this.form.badgeStyle,
          status: this.form.status,
          maxUses: this.form.maxUses === "" ? undefined : Number(this.form.maxUses),
          remark: this.form.remark || undefined
        }
        const res = await saveInviteCode(payload)
        this.setMessage(res.message || "已保存", false)
        if (!this.form.id && res.data && res.data.id) {
          this.startEdit(res.data)
        }
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "保存失败", true)
      } finally {
        this.saving = false
      }
    },
    async toggleStatus(row) {
      try {
        const nextStatus = row.status === 1 ? 0 : 1
        await updateInviteCodeStatus(row.id, nextStatus)
        await this.reload()
        this.setMessage(nextStatus === 1 ? "邀请码已启用" : "邀请码已停用", false)
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async removeCode(row) {
      if (!window.confirm(`确定删除邀请码 ${row.code} 吗？已绑定的用户头像下方标识会一起消失。`)) {
        return
      }
      try {
        await deleteInviteCode(row.id)
        if (this.form.id === row.id) this.startCreate()
        await this.reload()
        this.setMessage("邀请码已删除", false)
      } catch (error) {
        this.setMessage((error && error.message) || "删除失败", true)
      }
    },
    async viewUsers(row) {
      this.usersVisible = true
      this.usersLoading = true
      this.users = []
      this.usersTitle = `${row.code} · ${row.badgeText}`
      try {
        const res = await fetchInviteCodeUsers(row.id)
        this.users = res.data || []
      } catch (error) {
        this.users = []
      } finally {
        this.usersLoading = false
      }
    },
    setMessage(text, isError) {
      this.message = text
      this.messageError = !!isError
    }
  }
}
</script>

<style scoped>
.invite-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.invite-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 24px;
  border-radius: 16px;
  background: linear-gradient(120deg, #fff7e6 0%, #ffffff 60%);
  border: 1px solid #f0e2c4;
}

.invite-toolbar__title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.invite-toolbar__desc {
  margin-top: 8px;
  max-width: 720px;
  font-size: 13px;
  line-height: 20px;
  color: #6b7280;
}

.invite-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.invite-count {
  font-size: 13px;
  color: #8a6d1f;
}

.invite-card {
  padding: 20px 22px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #eceff3;
}

.invite-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.filter-input,
.field-input {
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #dfe3e8;
  font-size: 13px;
  color: #1f2937;
  box-sizing: border-box;
}

.filter-input {
  flex: 1;
  max-width: 360px;
}

.filter-select {
  height: 38px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #dfe3e8;
  font-size: 13px;
  color: #1f2937;
}

.primary-button,
.ghost-button {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
}

.primary-button {
  background: #111827;
  color: #ffffff;
}

.primary-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.ghost-button {
  background: #ffffff;
  border-color: #dfe3e8;
  color: #374151;
}

.list-state {
  padding: 26px 0;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
}

.invite-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.invite-table th,
.invite-table td {
  padding: 12px 10px;
  text-align: left;
  border-bottom: 1px solid #f1f3f6;
  vertical-align: middle;
}

.invite-table th {
  font-size: 12px;
  color: #8b93a1;
  font-weight: 600;
}

.code-text {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-weight: 700;
  color: #111827;
  letter-spacing: 1px;
}

.used-limit {
  color: #9ca3af;
}

.status-pill {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.status-pill--on {
  background: #e7f6ec;
  color: #1d8a4b;
}

.status-pill--off {
  background: #f2f4f7;
  color: #8b93a1;
}

.row-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.link-button {
  padding: 0;
  border: none;
  background: none;
  font-size: 13px;
  color: #2563eb;
  cursor: pointer;
}

.link-button--danger {
  color: #dc2626;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 14px;
}

.pager-text {
  font-size: 13px;
  color: #6b7280;
}

.editor-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.editor-eyebrow {
  font-size: 12px;
  color: #9ca3af;
}

.editor-head h2,
.modal-head h3 {
  margin: 6px 0 0;
  font-size: 18px;
  color: #111827;
}

.editor-hint {
  font-size: 12px;
  color: #9ca3af;
}

.editor-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field--full {
  grid-column: 1 / -1;
}

.field-label {
  font-size: 12px;
  color: #6b7280;
}

.editor-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fafc;
}

.editor-preview__label {
  font-size: 12px;
  color: #6b7280;
}

.badge-preview {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.badge-preview__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.85;
}

.badge-preview--gold {
  background: linear-gradient(120deg, #fff1cc, #ffe0a3);
  color: #8a5a09;
  border: 1px solid #f0d79b;
}

.badge-preview--purple {
  background: linear-gradient(120deg, #f3e8ff, #e6d6ff);
  color: #6d28d9;
  border: 1px solid #e2d3fb;
}

.badge-preview--blue {
  background: linear-gradient(120deg, #e6f1ff, #d6e7ff);
  color: #1d4ed8;
  border: 1px solid #d3e3fb;
}

.badge-preview--green {
  background: linear-gradient(120deg, #e7f6ec, #d6f0e0);
  color: #1d8a4b;
  border: 1px solid #d3eddd;
}

.editor-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 18px;
}

.editor-message {
  margin-right: auto;
  font-size: 13px;
  color: #1d8a4b;
}

.editor-message--error {
  color: #dc2626;
}

.modal-mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  z-index: 2000;
}

.modal-panel {
  width: min(560px, 92vw);
  max-height: 76vh;
  overflow-y: auto;
  padding: 20px 22px;
  border-radius: 16px;
  background: #ffffff;
}

.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
}

.user-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.user-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
}

.user-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.user-avatar--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e5e7eb;
  color: #4b5563;
  font-size: 14px;
}

.user-name {
  font-size: 14px;
  color: #111827;
  font-weight: 600;
}

.user-meta {
  margin-top: 2px;
  font-size: 12px;
  color: #9ca3af;
}
</style>
